import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    ORGANIGRAM_BEHIND_SCHEDULE_COLOUR,
    ORGANIGRAM_COMMITTED_COLOUR,
    ORGANIGRAM_TAG_COLOURS,
    organigramBehindScheduleGlyph,
} from 'Components/people/positionTypes';
import { organigramPdfOptions } from 'Components/workforce/organigramPdfDocument';
import OrganigramToolbar from 'Components/workforce/OrganigramToolbar';

import Alert from 'CommonComponents/Alert';

import OrgChart from 'Scripts/orgchart';

/*
 * The org chart node card, and the canvas it lives on.
 *
 * CARD LAYOUT
 *
 * A card is a white box with a left accent bar encoding the position type, a
 * committed dot in the top right, and two lines of text: role title (wrapping
 * to at most two lines) and start-to-end dates.
 *
 * A position that is behind schedule prints those same dates in alert red
 * behind a warning glyph. The chart library gives every node of a tag the same
 * fixed template, so the plain and the red dates are two separate fields
 * sharing one baseline: a field whose bound value is null or undefined is
 * skipped entirely, so whichever of the pair is bound is the one that draws.
 * The committed dot works the same way. (A field bound to an empty string
 * still renders its fragment, which is why null matters.)
 *
 * DROPS FROM OUTSIDE THE CHART
 *
 * The library handles dragging a node WITHIN the chart itself. A drop from the
 * unassigned panel is an HTML5 drag the canvas resolves on its own: every
 * rendered node carries a `data-n-id` attribute, so the node under the cursor
 * is whatever `document.elementFromPoint()` lands on, walked up to the nearest
 * one of those.
 *
 * DRAGGING A CARD OUT TO THE PANEL
 *
 * The reverse direction cannot be an HTML5 drag: the cards are SVG, which the
 * `draggable` attribute does not apply to, and the library owns the mousedown.
 * The library's drag is a plain mouse drag bound to the SVG, and it ends itself
 * on `mouseleave` — publishing a drop with no target the instant the cursor
 * clears the canvas, while the button is still down. That is the signal a card
 * is on its way out, so the chart reports it upwards and the panel, which owns
 * the ground the cursor is heading for, reads the release.
 */

const CARD_WIDTH = 201;
const CARD_RADIUS = 6;
/** Equal to the corner radius so the bar's right edge meets both rounded corners exactly. */
const ACCENT_WIDTH = CARD_RADIUS;
const TEXT_START = 14;

/** Baseline of the role title. A wrapped second line lands one line-height below. */
const ROLE_BASELINE = 21;
/** Baseline of the dates, clearing a title that has wrapped to two lines. */
const META_FIRST_BASELINE = 53;
const META_LINE_STEP = 15;
/** Space below the last baseline, clearing its descenders. */
const CARD_BOTTOM_PADDING = 14;

const COMMITTED_DOT_CX = 190;
const COMMITTED_DOT_CY = 11;
const COMMITTED_DOT_R = 4;
/** The show/edit links sit left of the committed dot so the two never overlap. */
const ACTIONS_X = 146;

/** The behind-schedule glyph is drawn in a 10×10 box; the dates start past it. */
const LATE_GLYPH_SIZE = 10;
const LATE_GLYPH_GUTTER = 14;

const COLOUR_TITLE = '#1B1C1D'; // charcoal.DEFAULT
const COLOUR_META = '#545556'; // charcoal.75
const COLOUR_DATES = '#8D8E8E'; // charcoal.50
const COLOUR_CARD_BORDER = '#C6C6C7'; // charcoal.25
const COLOUR_LINK = '#C6C6C7'; // charcoal.25
const COLOUR_CONTROL = '#055DF5'; // blue.DEFAULT
const COLOUR_STUB_FILL = '#F6F6F6'; // charcoal.10
const COLOUR_STUB_TEXT = '#6D6E70'; // charcoal.60

/**
 * Zoom floor. Below this the 12px role title renders under ~5px and the chart
 * stops being readable at all, so a very large chart stays at this scale and is
 * read by panning instead of shrinking further.
 */
const SCALE_MIN = 0.45;

/**
 * The drag payload type carried from an unassigned panel row. Checked rather
 * than read on dragover — the data itself is only readable on drop — so the
 * canvas ignores every other drag the browser reports.
 */
export const ORGANIGRAM_DRAG_MIME = 'application/x-inbudget-position';

/** The clone the library parks in the DOM while its OWN drag is in flight. */
const LIBRARY_DRAG_CLONE_ID = 'draging';

/**
 * Template tag → accent colour. `white` and `blue` predate the type colouring
 * and only ever carried paid nodes, so they share the paid accent.
 */
const TAG_ACCENTS = {
    ...ORGANIGRAM_TAG_COLOURS,
    white: ORGANIGRAM_TAG_COLOURS.grey,
    blue: ORGANIGRAM_TAG_COLOURS.grey,
};

/**
 * Card outline, then the accent bar drawn over its left edge. The bar is a path
 * rather than a rect so its own corners follow the card's radius — a square bar
 * would notch the rounded corner it sits in.
 */
function cardShape(height, accent) {
    const r = CARD_RADIUS;

    return (
        `<rect x="0.5" y="0.5" width="${CARD_WIDTH - 1}" height="${height - 1}" rx="${r}" ry="${r}" ` +
        `fill="#FFFFFF" stroke="${COLOUR_CARD_BORDER}" stroke-width="1"></rect>` +
        `<path d="M${ACCENT_WIDTH},0 A${r},${r} 0 0 0 0,${r} V${height - r} A${r},${r} 0 0 0 ${ACCENT_WIDTH},${height} Z" fill="${accent}"></path>`
    );
}

/** Where each line sits, and how tall the card has to be to hold them. */
function cardLayout({ withBudgetedStaff }) {
    const datesY = META_FIRST_BASELINE;
    const budgetedStaffY = withBudgetedStaff ? datesY + META_LINE_STEP : null;

    return {
        datesY,
        budgetedStaffY,
        height: (budgetedStaffY ?? datesY) + CARD_BOTTOM_PADDING,
    };
}

const arrow = (points) =>
    `<svg height="10px" width="10px" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 490 490" x="12px" y="9px">` +
    `<g><polygon points="${points}"/></g></svg>`;

const ARROW_DOWN = arrow('490,157.332 244.996,407.369 0,157.332 76.493,82.631 244.996,254.598 413.507,82.631');
const ARROW_UP = arrow('0,332.668 245.004,82.631 490,332.668 413.507,407.369 245.004,235.402 76.493,407.369');

/**
 * Build one node template.
 *
 * @param {string} accent  Left accent bar colour.
 * @param {Object} layout  From cardLayout().
 * @param {Object} options { hideActions, includePercentage }
 */
function nodeTemplate(accent, layout, { hideActions, includePercentage }) {
    const template = Object.assign({}, OrgChart.templates.ana);

    template.size = [CARD_WIDTH, layout.height];
    template.node = cardShape(layout.height, accent);

    // The title stops short of whatever occupies the card's top right.
    const titleWidth = (hideActions ? COMMITTED_DOT_CX - COMMITTED_DOT_R - 4 : ACTIONS_X - 4) - TEXT_START;

    template.name =
        `<text data-width="${titleWidth}" data-text-overflow="multiline-2-ellipsis" style="font-size: 12px;" ` +
        `fill="${COLOUR_TITLE}" font-weight="600" x="${TEXT_START}" y="${ROLE_BASELINE}" text-anchor="start">{val}</text>`;

    template.committedDot = `<circle cx="${COMMITTED_DOT_CX}" cy="${COMMITTED_DOT_CY}" r="${COMMITTED_DOT_R}" fill="${ORGANIGRAM_COMMITTED_COLOUR}"></circle>`;

    // The venue chart prints its allocation percentage at the end of the dates
    // line, so the dates give up the room it needs.
    const datesWidth = CARD_WIDTH - TEXT_START - (includePercentage ? 55 : 12);

    template.dates =
        `<text data-width="${datesWidth}" data-text-overflow="ellipsis" style="font-size: 10.5px;" ` +
        `fill="${COLOUR_DATES}" x="${TEXT_START}" y="${layout.datesY}" text-anchor="start">{val}</text>`;

    // The behind-schedule pair, sharing the dates baseline. Bottom-aligned to
    // it so the glyph sits on the text rather than above it.
    template.lateIcon = `<g transform="translate(${TEXT_START}, ${layout.datesY - LATE_GLYPH_SIZE + 1})">${organigramBehindScheduleGlyph()}</g>`;

    template.datesLate =
        `<text data-width="${datesWidth - LATE_GLYPH_GUTTER}" data-text-overflow="ellipsis" style="font-size: 10.5px;" ` +
        `fill="${ORGANIGRAM_BEHIND_SCHEDULE_COLOUR}" x="${TEXT_START + LATE_GLYPH_GUTTER}" y="${layout.datesY}" text-anchor="start">{val}</text>`;

    if (includePercentage) {
        template.percentage =
            `<text style="font-size: 11px;" font-weight="600" fill="${COLOUR_META}" ` +
            `x="${CARD_WIDTH - 12}" y="${layout.datesY}" text-anchor="end">{val}%</text>`;
    }

    if (layout.budgetedStaffY !== null) {
        template.budgetedStaffLabel =
            `<text data-width="${CARD_WIDTH - TEXT_START - 12}" data-text-overflow="ellipsis" style="font-size: 11px;" ` +
            `fill="${COLOUR_META}" font-weight="600" x="${TEXT_START}" y="${layout.budgetedStaffY}" text-anchor="start">{val}</text>`;
    }

    if (!hideActions) {
        template.html = `<foreignobject class="node" width="30px" height="14px" x="${ACTIONS_X}px" y="5px">{val}</foreignobject>`;
    }

    const pill =
        `<rect x="-11" y="5" height="21" width="46" rx="10" ry="10" stroke-width="1" fill="${COLOUR_CONTROL}" stroke="${COLOUR_CONTROL}"></rect>` +
        `<text text-anchor="middle" style="font-size: 12px;cursor:pointer;" font-weight="bold" fill="#ffffff" x="3" y="20">{children-total-count}</text>`;

    template.plus = pill + ARROW_DOWN;
    template.minus = pill + ARROW_UP;
    template.link = `<path stroke-linejoin="round" stroke="${COLOUR_LINK}" stroke-width="1px" fill="none" d="{rounded}" />`;

    return template;
}

/** The stub standing in for roles a type filter has hidden. */
function stubTemplate() {
    const template = Object.assign({}, OrgChart.templates.ana);

    template.size = [CARD_WIDTH, 30];
    template.node =
        `<rect x="0.5" y="0.5" width="${CARD_WIDTH - 1}" height="29" rx="${CARD_RADIUS}" ry="${CARD_RADIUS}" ` +
        `fill="${COLOUR_STUB_FILL}" stroke="${COLOUR_CARD_BORDER}" stroke-width="1" stroke-dasharray="4,2"></rect>`;
    template.name = `<text style="font-size: 11px;" fill="${COLOUR_STUB_TEXT}" x="${TEXT_START}" y="19" text-anchor="start">{val}</text>`;
    template.committedDot = '';
    template.dates = '';
    template.lateIcon = '';
    template.datesLate = '';
    template.html = '';
    template.percentage = '';
    template.budgetedStaffLabel = '';
    template.plus = '';
    template.minus = '';
    template.link = `<path stroke-linejoin="round" stroke="${COLOUR_LINK}" stroke-width="1px" fill="none" d="{rounded}" />`;

    return template;
}

/** Register a template per tag, and return the tag → template map the chart is configured with. */
function registerTemplates(options) {
    const tags = { stub: { template: 'stub' } };
    const layout = cardLayout({ withBudgetedStaff: options.includeBudgetedStaff });

    OrgChart.templates.stub = stubTemplate();

    Object.entries(TAG_ACCENTS).forEach(([tag, accent]) => {
        OrgChart.templates[tag] = nodeTemplate(accent, layout, options);
        tags[tag] = { template: tag };
    });

    return tags;
}

/**
 * Derive the card fields the chart needs but the API does not send: the
 * committed dot, and which of the plain / behind-schedule dates pair draws.
 *
 * Nodes are copied rather than mutated: the array belongs to the filter hook's
 * memo, and writing to it would make the chart's own state its input.
 */
export function withCardFields(nodes) {
    return (Array.isArray(nodes) ? nodes : []).map((node) => {
        const late = !node._isStub && node.isBehindSchedule === true;

        return {
            ...node,
            committedDot: Number(node.committed) > 0 ? 1 : null,
            lateIcon: late ? 1 : null,
            dates: late ? null : node.dates,
            datesLate: late ? node.dates : null,
        };
    });
}

/** The message the server sent, or a fallback when it sent none. */
export function serverMessage(error, fallback) {
    const message = error?.response?.data?.message;

    return typeof message === 'string' && message.trim() !== '' ? message : fallback;
}

function Chart({
    nodes,
    includePercentage = false,
    withoutPagination = false,
    hideActions = false,
    includeBudgetedStaff = false,
    acceptsExternalDrops = false,
    unassignedCount = 0,
    ...props
}) {
    const canvasRef = useRef(null);
    const divRef = useRef(null);
    const chartRef = useRef(null);
    const [zoomPercentage, setZoomPercentage] = useState(100);
    const [errorMessage, setErrorMessage] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);

    const css = '.boc-chart-menu div {display: flex} #tree div hr {margin-bottom: 3px}';

    const chartNodes = useMemo(() => withCardFields(nodes), [nodes]);
    const isEmpty = chartNodes.length === 0;

    /** Node id → role title, for naming the reporting line a drop would create. */
    const nodeNames = useMemo(() => new Map(chartNodes.map((node) => [String(node.id), node.name])), [chartNodes]);

    // The export document is rebuilt from the current nodes each time, so its
    // metrics and key describe the chart as it stands rather than as it loaded.
    const exportContextRef = useRef(props.exportContext);
    exportContextRef.current = props.exportContext;

    const treeUpdateUrlRef = useRef(props.treeUpdateUrl);
    treeUpdateUrlRef.current = props.treeUpdateUrl;

    const onTreeUpdatedRef = useRef(props.onTreeUpdated);
    onTreeUpdatedRef.current = props.onTreeUpdated;

    const onExternalDropRef = useRef(props.onExternalDrop);
    onExternalDropRef.current = props.onExternalDrop;

    const onDragOffChartRef = useRef(props.onDragOffChart);
    onDragOffChartRef.current = props.onDragOffChart;

    /**
     * A clean copy of the nodes as the database still holds them, used to put
     * the picture back when a drag is refused. Rebuilt from `chartNodes` rather
     * than read out of the chart, whose own array the library has already
     * mutated by the time the rejection arrives.
     */
    const restoreNodes = useCallback(() => {
        chartRef.current?.load(chartNodes.map((node) => ({ ...node })));
    }, [chartNodes]);

    const restoreNodesRef = useRef(restoreNodes);
    restoreNodesRef.current = restoreNodes;

    const zoomIn = useCallback(() => chartRef.current?.zoom(true, [50, 50], true), []);
    const zoomOut = useCallback(() => chartRef.current?.zoom(false, [50, 50], true), []);
    const fit = useCallback(() => chartRef.current?.fit(), []);

    /** Centre the chart in the viewport at the current zoom. */
    const recentre = useCallback(() => {
        const chart = chartRef.current;

        if (!chart) {
            return;
        }

        const boundary = chart.response?.boundary;

        if (!boundary) {
            chart.fit();

            return;
        }

        const viewBox = chart.getViewBox().slice(0);
        viewBox[0] = (boundary.minX + boundary.maxX) / 2 - viewBox[2] / 2;
        viewBox[1] = (boundary.minY + boundary.maxY) / 2 - viewBox[3] / 2;
        chart.setViewBox(viewBox);
        chart.draw(OrgChart.action.pan);
    }, []);

    const resetZoom = useCallback(() => {
        const chart = chartRef.current;

        if (!chart) {
            return;
        }

        chart.setScale(1);
        chart.draw(OrgChart.action.zoom);
    }, []);

    const exportPdf = useCallback(() => {
        const chart = chartRef.current;

        if (!chart) {
            return;
        }

        OrgChart.pdfPrevUI.show(chart, organigramPdfOptions(chart.config.nodes, exportContextRef.current ?? {}));
    }, []);

    /** Persist a node dragged within the chart, and put it back if the server refuses. */
    const updateNode = useCallback((node) => {
        axios
            .put(treeUpdateUrlRef.current, {
                id: parseInt(node.id),
                pid: node.pid === '' || node.pid === null || node.pid === undefined ? null : parseInt(node.pid),
            })
            .then(() => {
                // A move can leave the position it left behind with neither a
                // supervisor nor a subordinate, which puts it in the panel.
                onTreeUpdatedRef.current?.();
            })
            .catch((error) => {
                setErrorMessage(serverMessage(error, $t('That reporting line could not be saved. The chart has been returned to its previous shape.')));
                restoreNodesRef.current();
            });
    }, []);

    useEffect(() => {
        if (isEmpty) {
            return undefined;
        }

        const tags = registerTemplates({ hideActions, includePercentage, includeBudgetedStaff });

        const chart = new OrgChart(divRef.current, {
            nodes: chartNodes.map((node) => ({ ...node })),
            enableDragDrop: true,
            enableSearch: false,
            nodeMouseClick: OrgChart.action.none,
            template: 'blue',
            tags,
            nodeBinding: {
                name: 'name',
                dates: 'dates',
                datesLate: 'datesLate',
                lateIcon: 'lateIcon',
                committedDot: 'committedDot',
                ...(includeBudgetedStaff && { budgetedStaffLabel: 'budgetedStaffLabel' }),
                ...(!hideActions && { html: 'html' }),
                ...(includePercentage && { percentage: 'percentage' }),
            },
            zoom: { speed: 30, smooth: 5 },
            // Ctrl+scroll (and trackpad pinch, which the browser reports as a
            // ctrl-modified wheel) zooms; a plain wheel scrolls the page.
            mouseScrool: OrgChart.action.ctrlZoom,
            // Fit and centre on every build — and the chart is rebuilt whenever
            // the nodes change, which covers a collapse level, filter or cost
            // centre change as well as first paint.
            scaleInitial: OrgChart.match.boundary,
            scaleMin: SCALE_MIN,
            // `sticky` snaps the view back inside the chart's boundary after a
            // pan, so the chart can never be dragged fully out of sight.
            sticky: true,
            // The chart owns its own toolbar, rendered over the canvas.
            toolbar: false,
            collapse: {
                level: props.collapseLevel ? props.collapseLevel : 3,
            },
            ...(withoutPagination && {
                showXScroll: OrgChart.scroll.visible,
                layout: OrgChart.mixed,
            }),
        });

        chartRef.current = chart;

        chart.onUpdateNode(function (args) {
            updateNode(args.newData);
        });

        // A card dragged clear of the canvas, on its way to the panel. The
        // library reports it as a drop with no target — which it also does for
        // a release onto blank canvas, so the still-held button is what
        // separates the two. A release changes nothing and must stay a no-op.
        chart.onDrop(function (args) {
            if (args.dropId !== null && args.dropId !== undefined) {
                return;
            }

            if (!(args.event && args.event.buttons & 1)) {
                return;
            }

            onDragOffChartRef.current?.(Number(args.dragId));
        });

        const syncZoom = () => setZoomPercentage(Math.round(chart.getScale() * 100));

        chart.onRedraw(syncZoom);
        syncZoom();

        // A wheel zoom updates the view immediately but only settles into a
        // redraw half a second later, so the readout is refreshed directly.
        const element = divRef.current;
        let frame = null;
        const onWheel = () => {
            if (frame !== null) {
                return;
            }
            frame = requestAnimationFrame(() => {
                frame = null;
                syncZoom();
            });
        };
        element.addEventListener('wheel', onWheel, { passive: true });

        const onDoubleClick = (event) => {
            // Leave the show/edit links to the browser.
            if (event.target.closest && event.target.closest('a')) {
                return;
            }
            recentre();
        };
        element.addEventListener('dblclick', onDoubleClick);

        return () => {
            element.removeEventListener('wheel', onWheel);
            element.removeEventListener('dblclick', onDoubleClick);
            if (frame !== null) {
                cancelAnimationFrame(frame);
            }
            chartRef.current = null;
            chart.destroy();
        };
    }, [chartNodes, isEmpty, hideActions, includePercentage, includeBudgetedStaff, withoutPagination, props.collapseLevel, recentre, updateNode]);

    /**
     * Resolve the chart node under the cursor.
     *
     * A drop must land on a card. Blank canvas is not a target: for a lone
     * position, clearing an already-null supervisor changes nothing, so
     * accepting it would report success and do nothing at all. The first
     * reporting line is made by dropping one panel row onto another.
     */
    const dropTargetAt = useCallback(
        (event) => {
            const canvas = canvasRef.current;

            if (!canvas) {
                return null;
            }

            const element = document.elementFromPoint(event.clientX, event.clientY)?.closest?.('[data-n-id]');
            const id = element?.getAttribute('data-n-id');

            if (!id || id === LIBRARY_DRAG_CLONE_ID) {
                return null;
            }

            const rect = element.getBoundingClientRect();
            const canvasRect = canvas.getBoundingClientRect();

            return {
                id,
                name: nodeNames.get(String(id)) ?? '',
                left: rect.left - canvasRect.left,
                top: rect.top - canvasRect.top,
                width: rect.width,
                height: rect.height,
            };
        },
        [nodeNames],
    );

    /**
     * Disarm the card that was dragged off the canvas, wherever it is released.
     *
     * On `window` rather than `document` deliberately: React delegates its own
     * events at the document, so a release onto the panel reaches the panel's
     * handler — which is what turns it into a drop — before the arming is
     * dropped here.
     */
    useEffect(() => {
        if (!acceptsExternalDrops) {
            return undefined;
        }

        const onMouseUp = () => onDragOffChartRef.current?.(null);

        window.addEventListener('mouseup', onMouseUp);

        return () => window.removeEventListener('mouseup', onMouseUp);
    }, [acceptsExternalDrops]);

    useEffect(() => {
        const canvas = canvasRef.current;

        if (!acceptsExternalDrops || !canvas) {
            return undefined;
        }

        const carriesPosition = (event) => Array.from(event.dataTransfer?.types ?? []).includes(ORGANIGRAM_DRAG_MIME);

        const onDragOver = (event) => {
            if (!carriesPosition(event)) {
                return;
            }

            // Without preventDefault the browser refuses the drop outright.
            event.preventDefault();

            const target = dropTargetAt(event);
            event.dataTransfer.dropEffect = target ? 'move' : 'none';

            setDropTarget((current) => (current?.id === target?.id ? current : target));
        };

        const onDragLeave = (event) => {
            if (!canvas.contains(event.relatedTarget)) {
                setDropTarget(null);
            }
        };

        const onDrop = (event) => {
            if (!carriesPosition(event)) {
                return;
            }

            event.preventDefault();

            const target = dropTargetAt(event);
            const positionId = event.dataTransfer.getData(ORGANIGRAM_DRAG_MIME);

            setDropTarget(null);

            if (!target || !positionId) {
                return;
            }

            setErrorMessage(null);

            Promise.resolve(onExternalDropRef.current?.(Number(positionId), Number(target.id))).catch((error) => {
                setErrorMessage(serverMessage(error, $t('That position could not be assigned. Please try again.')));
            });
        };

        canvas.addEventListener('dragover', onDragOver);
        canvas.addEventListener('dragleave', onDragLeave);
        canvas.addEventListener('drop', onDrop);

        return () => {
            canvas.removeEventListener('dragover', onDragOver);
            canvas.removeEventListener('dragleave', onDragLeave);
            canvas.removeEventListener('drop', onDrop);
        };
    }, [acceptsExternalDrops, dropTargetAt]);

    return (
        <Fragment>
            <style nonce={window.nonceKey}>{css}</style>
            {/* The dashed boundary and tinted fill are always drawn, so an empty
                canvas reads as an empty chart rather than as a failed load. */}
            <div
                ref={canvasRef}
                className="tw-relative tw-h-full tw-min-h-96 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-charcoal-25 tw-bg-charcoal-10"
            >
                {!isEmpty && (
                    <OrganigramToolbar
                        zoomPercentage={zoomPercentage}
                        onZoomIn={zoomIn}
                        onZoomOut={zoomOut}
                        onFit={fit}
                        onRecentre={recentre}
                        onResetZoom={resetZoom}
                        onExport={exportPdf}
                    />
                )}
                {/* Anchored to the bottom, where the toolbar is not: the toolbar
                    takes the top-end corner and a narrow canvas leaves no room
                    beside it. */}
                {errorMessage && (
                    <div className="tw-absolute tw-bottom-3 tw-start-3 tw-end-3 tw-z-20 tw-max-w-md">
                        <Alert type="error">
                            <span className="tw-flex tw-items-start tw-gap-x-3">
                                <span>{errorMessage}</span>
                                <button
                                    type="button"
                                    onClick={() => setErrorMessage(null)}
                                    className="tw-shrink-0 tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-underline focus:tw-outline-none"
                                >
                                    {$t('Dismiss')}
                                </button>
                            </span>
                        </Alert>
                    </div>
                )}
                {dropTarget && (
                    <div className="tw-pointer-events-none tw-absolute tw-z-10" style={{ left: dropTarget.left, top: dropTarget.top }}>
                        <div
                            className="tw-rounded-md tw-border-2 tw-border-dashed tw-border-blue"
                            style={{ width: dropTarget.width, height: dropTarget.height }}
                        />
                        <div
                            className="tw-mt-1 tw-rounded tw-bg-white tw-px-2 tw-py-1 tw-text-2xs tw-shadow-primary"
                            style={{ maxWidth: Math.max(dropTarget.width, 160) }}
                        >
                            <span className="tw-text-charcoal-75">{$t('Drop here to report to')}</span>
                            <span className="tw-block tw-font-semibold tw-text-blue">{dropTarget.name}</span>
                        </div>
                    </div>
                )}
                {/* Across the top, not in the middle of the canvas: the first
                    thing a reader needs on an unstarted chart is what to do,
                    and centred body text in an empty area reads as decoration. */}
                {isEmpty && acceptsExternalDrops && unassignedCount > 0 && (
                    <div className="tw-absolute tw-inset-x-0 tw-top-0 tw-z-20 tw-rounded-t-lg tw-border-0 tw-border-b tw-border-solid tw-border-blue tw-bg-blue tw-bg-opacity-10 tw-px-4 tw-py-3 tw-text-center">
                        <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-text-charcoal">{$t('Start this chart')}</p>
                        <p className="tw-mb-0 tw-text-2xs tw-text-charcoal-75">
                            {$t(
                                'Drag one position in the panel onto another to create the first reporting line. Everything else can then be dragged onto the chart.',
                            )}
                        </p>
                    </div>
                )}
                {isEmpty ? (
                    <div className="tw-flex tw-h-full tw-min-h-96 tw-flex-col tw-items-center tw-justify-center tw-gap-y-1 tw-px-6 tw-text-center">
                        <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-charcoal">{$t('No positions on this chart')}</p>
                        <p className="tw-mb-0 tw-text-xs tw-text-charcoal-75">
                            {acceptsExternalDrops && unassignedCount > 0
                                ? $t('Every position here is unassigned.')
                                : $t('Positions appear here once they are added and given a reporting line.')}
                        </p>
                    </div>
                ) : (
                    <div id="tree" className="tw-h-full" ref={divRef}></div>
                )}
            </div>
        </Fragment>
    );
}

export default Chart;
